import React from 'react';
import { Chip, Avatar, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Person as PersonIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  Check as CheckIcon
} from '@mui/icons-material';

const StyledInputChip = styled(Chip)(({ theme, variant, hasLeadingIcon, hasTrailingIcon }) => ({
  height: '32px',
  borderRadius: '16px',
  fontSize: '14px',
  fontWeight: 500,
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  
  // Label only variant
  ...(variant === 'label-only' && {
    backgroundColor: 'transparent',
    border: '1px solid #E0E0E0',
    color: '#424242',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#BDBDBD',
    },
  }),
  
  // Label with trailing icon
  ...(variant === 'label-trailing' && {
    backgroundColor: 'transparent',
    border: '1px solid #E0E0E0',
    color: '#424242',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#BDBDBD',
    },
  }),
  
  // Leading icon with label
  ...(variant === 'leading-label' && {
    backgroundColor: 'transparent',
    border: '1px solid #E0E0E0',
    color: '#424242',
 
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#BDBDBD',
    },
  }),
  
  // Leading icon, label, trailing icon
  ...(variant === 'leading-trailing' && {
    backgroundColor: 'transparent',
    border: '1px solid #E0E0E0',
    color: '#424242',
 
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#BDBDBD',
    },
  }),
  
  // Label with avatar
  ...(variant === 'label-avatar' && {
    backgroundColor: 'transparent',
    border: '1px solid #E0E0E0',
    color: '#424242',
 
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#BDBDBD',
    },
  }),
  
  // Avatar, label, trailing icon
  ...(variant === 'avatar-trailing' && {
    backgroundColor: 'transparent',
    border: '1px solid #E0E0E0',
    color: '#424242',
   
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#BDBDBD',
    },
  }),
  
  // Selected/Active state
  '&.selected': {
    backgroundColor: '#E3F2FD',
    borderColor: '#1976D2',
    color: '#1976D2',
  },
  
  // Icon styling
  '& .MuiChip-icon': {
    fontSize: '16px',
    marginLeft: '8px',
    marginRight: '-4px',
    color: 'inherit',
  },
  
  // Avatar styling
  '& .MuiChip-avatar': {
    width: '24px',
    height: '24px',
    fontSize: '12px',
    marginLeft: '4px',
    marginRight: '-4px',
  },
  
  // Delete icon styling
  '& .MuiChip-deleteIcon': {
    fontSize: '16px',
    marginLeft: '4px',
    marginRight: '-6px',
    color: 'inherit',
    '&:hover': {
      color: 'inherit',
      opacity: 0.8,
    },
  },
  
  // Label styling
  '& .MuiChip-label': {
 
    fontSize: '14px',
    fontWeight: 500,
  },
}));

const InputClip = ({ 
  label = "Label",
  variant = "label-only",  
  leadingIcon,
  trailingIcon,
  avatar,
  avatarSrc,
  avatarText,
  selected = false,
  onDelete,
  onTrailingIconClick,
  onClick,
  style,
  ...props 
}) => {
  // Get leading icon
  const getLeadingIcon = () => {
    if (leadingIcon) return leadingIcon;
    if (variant.includes('leading')) return <LockIcon />;
    return undefined;
  };

  // Get trailing icon
  const getTrailingIcon = () => {
    if (trailingIcon) return trailingIcon;
    if (variant.includes('trailing')) return <CloseIcon />;
    return undefined;
  };

  // Get avatar
  const getAvatar = () => {
    if (avatar) return avatar;
    if (avatarSrc) return <Avatar src={avatarSrc} />;
    if (avatarText) return <Avatar>{avatarText}</Avatar>;
    if (variant.includes('avatar') && !avatar && !avatarSrc && !avatarText) {
      return <Avatar><PersonIcon /></Avatar>;
    }
    return undefined;
  };

  const handleClick = (event) => {
    if (onClick) {
      onClick(event);
    }
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    if (onDelete) {
      onDelete(event);
    }
  };

  const handleTrailingIconClick = (event) => {
    event.stopPropagation();
    if (onTrailingIconClick) {
      onTrailingIconClick(event);
    }
  };

  // If we need a trailing icon that's not a delete, we need to handle it differently
  const hasCustomTrailingIcon = getTrailingIcon() && !onDelete;

  if (hasCustomTrailingIcon) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          position: 'relative',
          ...style
        }}
      >
        <StyledInputChip
          label={label}
          variant={variant}
          className={selected ? 'selected' : ''}
          icon={getLeadingIcon()}
          avatar={getAvatar()}
          onClick={handleClick}
          clickable={!!onClick}
          sx={{
            paddingRight: hasCustomTrailingIcon ? '32px' : undefined,
          }}
          {...props}
        />
        {hasCustomTrailingIcon && (
          <Box
            onClick={handleTrailingIconClick}
            sx={{
              position: 'absolute',
              right: '8px',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: 'inherit',
              fontSize: '16px',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            {getTrailingIcon()}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <StyledInputChip
      label={label}
      variant={variant}
      className={selected ? 'selected' : ''}
      icon={getLeadingIcon()}
      avatar={getAvatar()}
      onDelete={onDelete ? handleDelete : undefined}
      onClick={handleClick}
      style={style}
      clickable={!!onClick}
      {...props}
    />
  );
};

export default InputClip;
